import { Block, HiVis, Design } from "@/types/editor/blocks";
import { characterShapes, Characters } from "@/config/font";

// Debounce helper - limits how often a function can be called
const debounceTimers: { [key: string]: NodeJS.Timeout } = {};

/**
 * Performs a debounced update to prevent excessive re-rendering
 * @param key Unique identifier for this debounce operation
 * @param callback Function to call after debounce period
 * @param delay Milliseconds to wait before executing
 */
export const debounceUpdate = (
  key: string,
  callback: () => void,
  delay: number = 200 // Increased delay to reduce frequency
) => {
  // Clear existing timer if any
  if (debounceTimers[key]) {
    clearTimeout(debounceTimers[key]);
  }

  // Set new timer
  debounceTimers[key] = setTimeout(() => {
    callback();
    delete debounceTimers[key];
  }, delay);
};

/**
 * Debounced version of HiVis updates
 * Use this for slider updates and other frequent changes
 */
export const debouncedUpdateHiVis = (
  hiVis: HiVis,
  onUpdate: (updatedHiVis: HiVis) => void,
  delay: number = 200 // Increased delay to reduce frequency
) => {
  const key = `hivis-${hiVis.id || "default"}`;
  debounceUpdate(
    key,
    () => {
      onUpdate(hiVis);
    },
    delay
  );
};

/**
 * Creates a deep copy of a HiVis object to prevent mutation issues
 */
export const cloneHiVis = (hiVis: HiVis): HiVis => {
  return JSON.parse(JSON.stringify(hiVis));
};

/**
 * Get the natural width ratio for a character
 */
export const getNaturalWidthRatio = (character: string): number => {
  if (!character) return 1;
  const char = character.toLowerCase();
  if (char === " ") return 0.5; // Space is half width
  if (char === ".") return 0.2; // Dot is very narrow
  if (char === "-") return 0.5; // Dash is half width

  const shape = characterShapes[char as Characters];
  if (!shape) return 1;

  const [, , vbWidth, vbHeight] = shape.viewBox.split(" ").map(Number);
  return vbWidth / vbHeight;
};

/**
 * Update HiVis design based on asset number changes
 */
export const updateHiVisDesign = (hiVis: HiVis): HiVis => {
  const { assetNumber, design, stackFirstTwo, naturalWidth } = hiVis;

  // Create deep copy of design to prevent mutation
  const updatedDesign: Design = JSON.parse(JSON.stringify(design));

  // Find the character container in the design structure
  // Typically this is in the first block's blocks (for simple designs)
  let characterContainer: Block | null = null;
  let characterContainerPath: number[] = [];

  // Helper function to find the character container
  const findCharacterContainer = (
    blocks: Block[],
    currentPath: number[] = []
  ): { container: Block | null; path: number[] } => {
    for (let i = 0; i < blocks.length; i++) {
      const block = blocks[i];
      const path = [...currentPath, i];

      // Check if this block's children contain character blocks
      if (block.blocks?.some((b) => b.character !== undefined)) {
        return { container: block, path };
      }

      // Recursively check nested blocks
      if (block.blocks && block.blocks.length > 0) {
        const result = findCharacterContainer(block.blocks, path);
        if (result.container) {
          return result;
        }
      }
    }

    return { container: null, path: [] };
  };

  const result = findCharacterContainer(updatedDesign.blocks);
  characterContainer = result.container;
  characterContainerPath = result.path;

  // If no character container found, try to find the innermost container
  if (!characterContainer) {
    // Find the deepest nested container that doesn't have any blocks
    const findDeepestContainer = (
      blocks: Block[],
      currentPath: number[] = []
    ): { container: Block | null; path: number[] } => {
      for (let i = 0; i < blocks.length; i++) {
        const block = blocks[i];
        const path = [...currentPath, i];

        // Skip non-container blocks
        if (!block.isContainer) continue;

        // Check if this is a leaf container (has no blocks or empty blocks array)
        if (!block.blocks || block.blocks.length === 0) {
          return { container: block, path };
        }

        // If it has blocks, check if any of them are containers
        const hasContainerChildren = block.blocks.some((b) => b.isContainer);

        if (!hasContainerChildren) {
          // If no container children, use this one
          return { container: block, path };
        }

        // Recursively check nested blocks
        if (block.blocks && block.blocks.length > 0) {
          const result = findDeepestContainer(block.blocks, path);
          if (result.container) {
            return result;
          }
        }
      }

      // If nothing better found, use the first top-level container
      for (let i = 0; i < blocks.length; i++) {
        if (blocks[i].isContainer) {
          return { container: blocks[i], path: [i] };
        }
      }

      return { container: null, path: [] };
    };

    // Try to find a suitable container
    const containerResult = findDeepestContainer(updatedDesign.blocks);
    characterContainer = containerResult.container;
    characterContainerPath = containerResult.path;

    // If still no container, use the first block (should be rare)
    if (!characterContainer && updatedDesign.blocks.length > 0) {
      characterContainer = updatedDesign.blocks[0];
      characterContainerPath = [0];

      // Ensure it's marked as a container
      characterContainer.isContainer = true;

      // Create empty blocks array if needed
      if (!characterContainer.blocks) {
        characterContainer.blocks = [];
      }
    }

    console.warn("No character container found, using fallback container");
  }

  // If we still have no container, this is an invalid design
  if (!characterContainer) {
    console.error("Invalid design with no containers");
    return hiVis;
  }

  // Get the current characters and their properties
  const currentCharBlocks =
    characterContainer.blocks?.filter((b) => !b.isContainer) || [];

  // Save existing gap values to preserve them
  const existingGaps: number[] = [];
  if (characterContainer.blocks) {
    characterContainer.blocks.forEach((block, index) => {
      if (index < characterContainer.blocks!.length - 1) {
        // Store the gap that exists after this block
        const gapValue = block.gapLocked
          ? design.global_gap || 0
          : (block.gap ?? 0);
        existingGaps.push(gapValue);
      }
    });
  }

  // Convert asset number to an array of characters
  let characters: string[] = assetNumber.split("");

  // Create new character blocks array
  const newCharBlocks: Block[] = [];

  // Get container dimensions
  const containerWidth = characterContainer.width;
  const containerHeight = characterContainer.height;

  // Check if container is vertical
  const isVertical = containerWidth < containerHeight;

  // Calculate global gap to use for spacing
  const global_gap = design.global_gap || 0;

  // Handle the case where assetNumber is empty
  if (characters.length === 0) {
    // Create a placeholder block that takes up the full container
    // This ensures the container stays visible even when empty
    const placeholder: Block = {
      width: containerWidth,
      height: containerHeight,
      color: characterContainer.color,
      isContainer: false, // This is not a container
      gap: 0,
      gapLocked: true,
      // Don't add a character property to avoid displaying anything
    };

    // Add the placeholder block to maintain container structure
    newCharBlocks.push(placeholder);
  } else {
    // Handle stacking first two characters if enabled and we have at least 2 chars
    if (stackFirstTwo && characters.length >= 2) {
      // Create a special container for the first two characters
      const firstTwoChars = characters.slice(0, 2);
      const restOfChars = characters.slice(2);

      // For stacked characters, we'll use the natural width ratio of the wider character
      // divided by the total natural ratio of all characters to determine its width
      const firstCharRatio = getNaturalWidthRatio(firstTwoChars[0]);
      const secondCharRatio = getNaturalWidthRatio(firstTwoChars[1]);
      const maxStackedCharRatio = Math.max(firstCharRatio, secondCharRatio);

      // Calculate the total natural width ratio for all characters including stacked
      const allCharRatios = [maxStackedCharRatio];
      restOfChars.forEach((char) => {
        allCharRatios.push(getNaturalWidthRatio(char));
      });

      const totalRatio = allCharRatios.reduce((sum, ratio) => sum + ratio, 0);

      // Calculate width proportionally based on the natural width ratio
      const stackContainerWidth = isVertical
        ? containerWidth
        : naturalWidth
          ? containerWidth * (maxStackedCharRatio / totalRatio)
          : containerWidth / characters.length;

      // Create individual character blocks for the stacked characters
      const stackedCharBlocks: Block[] = firstTwoChars.map((char, index) => {
        return {
          width: stackContainerWidth,
          height: containerHeight / 2, // Half height because they're stacked
          character: char,
          color: characterContainer.color,
          gap: 0, // No gap for vertically stacked characters
          gapLocked: true,
          // For the first character in the stack, add a small gap at the bottom
          verticalGap: index === 0 ? global_gap : 0,
        };
      });

      // Create a container for the stacked characters
      const stackedContainer: Block = {
        width: stackContainerWidth,
        height: containerHeight,
        color: characterContainer.color,
        isContainer: true,
        blocks: stackedCharBlocks,
        // Don't include global_gap here to avoid redundancy
        verticalStack: true, // Mark this container as vertically stacked
        verticalGap: global_gap, // Use the global gap for vertical spacing
        gap: existingGaps.length > 0 ? existingGaps[0] : global_gap, // Use saved gap if available
        gapLocked: true, // Lock to global gap for consistency
      };

      // Add the stacked container as the first block
      newCharBlocks.push(stackedContainer);

      // Process the rest of the characters as normal
      characters = restOfChars;
    }

    // Calculate available width or height for remaining characters
    const remainingWidth = isVertical
      ? containerWidth
      : containerWidth -
        (newCharBlocks.length > 0
          ? newCharBlocks[0].width + (newCharBlocks[0].gap || global_gap)
          : 0);

    const charactersToProcess = characters.length;

    // If it's a vertical layout
    if (isVertical) {
      // Calculate total gap space for vertical layout
      const totalGapSpace = (charactersToProcess - 1) * global_gap;

      // Available height for characters
      const availableHeight = containerHeight - totalGapSpace;
      const charHeight = availableHeight / charactersToProcess;

      // Create blocks with fixed height and natural width if enabled
      for (let i = 0; i < charactersToProcess; i++) {
        const char = characters[i];

        // Get offset for existing gaps due to stacked container
        const gapOffset = newCharBlocks.length;

        // Get existing gap for this character if available
        const existingGapIndex = i + gapOffset;
        const existingGap =
          existingGapIndex < existingGaps.length
            ? existingGaps[existingGapIndex]
            : null;

        // Reuse existing block properties if possible
        const existingBlock = currentCharBlocks[newCharBlocks.length + i];

        // Create basic block with full container width
        const charBlock: Block = {
          width: containerWidth,
          height: charHeight,
          character: char,
          color: characterContainer.color,
          gap:
            i < charactersToProcess - 1
              ? existingGap !== null
                ? existingGap
                : global_gap
              : 0,
          gapLocked: existingBlock?.gapLocked || true,
        };

        // If natural width is enabled, calculate width based on character ratio
        if (naturalWidth) {
          const naturalRatio = getNaturalWidthRatio(char);
          const naturalWidth = charHeight * naturalRatio;

          // Only update width if natural width is less than container width
          if (naturalWidth < containerWidth) {
            charBlock.width = naturalWidth;
            // Later in rendering, this will be centered
          }
        }

        newCharBlocks.push(charBlock);
      }
    } else if (naturalWidth && charactersToProcess > 0) {
      // Horizontal layout with natural width

      // If we have stacked characters, we already calculated their width
      // based on natural width proportions, so we need to ensure the remaining
      // characters follow the same proportional distribution

      // Calculate natural width ratios for all remaining characters
      const characterRatios = characters.map((char) =>
        getNaturalWidthRatio(char)
      );
      const totalRatioRemaining = characterRatios.reduce(
        (sum, ratio) => sum + ratio,
        0
      );

      // Calculate total gap space for horizontal layout
      const totalGapSpace = (charactersToProcess - 1) * global_gap;

      // Available width for characters
      const availableWidth = remainingWidth - totalGapSpace;

      // Create blocks with proportional widths
      for (let i = 0; i < charactersToProcess; i++) {
        const char = characters[i];
        const naturalRatio = getNaturalWidthRatio(char);

        // Calculate width proportionally, allowing it to go to 0 if needed
        const width = Math.max(
          0, // Allow width to go to 0 instead of forcing 20mm minimum
          Math.floor((availableWidth * naturalRatio) / totalRatioRemaining)
        );

        // Get offset for existing gaps due to stacked container
        const gapOffset = newCharBlocks.length;

        // Get existing gap for this character if available
        const existingGapIndex = i + gapOffset;
        const existingGap =
          existingGapIndex < existingGaps.length
            ? existingGaps[existingGapIndex]
            : null;

        // Reuse existing block properties if possible
        const existingBlock = currentCharBlocks[newCharBlocks.length + i];

        newCharBlocks.push({
          width,
          height: containerHeight,
          character: char,
          color: characterContainer.color,
          gap:
            i < charactersToProcess - 1
              ? existingGap !== null
                ? existingGap
                : global_gap
              : 0,
          gapLocked: existingBlock?.gapLocked || true,
        });
      }
    } else if (charactersToProcess > 0) {
      // Default: uniform distribution in horizontal layout
      const totalGapSpace = (charactersToProcess - 1) * global_gap;

      // Calculate character width, allowing it to go to 0 if gaps consume all space
      const availableWidthForChars = remainingWidth - totalGapSpace;
      const charWidth = Math.max(
        0, // Allow width to go to 0 instead of forcing 20mm minimum
        Math.floor(availableWidthForChars / charactersToProcess)
      );

      // Create blocks with equal widths
      for (let i = 0; i < charactersToProcess; i++) {
        // Get offset for existing gaps due to stacked container
        const gapOffset = newCharBlocks.length;

        // Get existing gap for this character if available
        const existingGapIndex = i + gapOffset;
        const existingGap =
          existingGapIndex < existingGaps.length
            ? existingGaps[existingGapIndex]
            : null;

        // Reuse existing block properties if possible
        const existingBlock = currentCharBlocks[newCharBlocks.length + i];

        newCharBlocks.push({
          width: charWidth,
          height: containerHeight,
          character: characters[i],
          color: characterContainer.color,
          gap:
            i < charactersToProcess - 1
              ? existingGap !== null
                ? existingGap
                : global_gap
              : 0,
          gapLocked: existingBlock?.gapLocked || true,
        });
      }
    }
  }

  // Update the character container with new blocks
  characterContainer.blocks = newCharBlocks;

  // Helper function to update nested blocks in the design structure
  const updateNestedBlock = (
    blocks: Block[],
    path: number[],
    index: number,
    newContainer: Block
  ): Block[] => {
    if (index >= path.length) {
      return blocks;
    }

    const blockIndex = path[index];
    const updatedBlocks = [...blocks];

    if (index === path.length - 1) {
      // We've reached the character container

      // Ensure all stacked containers have their gap properties set correctly
      if (newContainer.blocks) {
        newContainer.blocks = newContainer.blocks.map((block) => {
          if (block.isContainer && block.verticalStack) {
            return {
              ...block,
              gap: design.global_gap || 0,
              gapLocked: true,
            };
          }
          return block;
        });
      }

      updatedBlocks[blockIndex] = newContainer;
      return updatedBlocks;
    }

    // Update child blocks recursively
    const blockToUpdate = { ...updatedBlocks[blockIndex] };

    if (!blockToUpdate.blocks) {
      blockToUpdate.blocks = [];
    }

    blockToUpdate.blocks = updateNestedBlock(
      blockToUpdate.blocks,
      path,
      index + 1,
      newContainer
    );

    updatedBlocks[blockIndex] = blockToUpdate;
    return updatedBlocks;
  };

  // Apply the changes to the design structure
  updatedDesign.blocks = updateNestedBlock(
    updatedDesign.blocks,
    characterContainerPath,
    0,
    characterContainer
  );

  // Extract gaps from character blocks for hiVis.gap property
  const extractGaps = (blocks: Block[]): number[] => {
    const gaps: number[] = [];

    blocks.forEach((block) => {
      if (block.blocks) {
        // Analyze all blocks to extract gaps between them
        const allBlocks = block.blocks;
        let hasStackedContainer = false;

        // First identify if we have a stacked container
        if (
          allBlocks.length > 0 &&
          allBlocks[0].isContainer &&
          allBlocks[0].verticalStack
        ) {
          hasStackedContainer = true;
        }

        // Process all blocks in sequence
        for (let i = 0; i < allBlocks.length - 1; i++) {
          const currentBlock = allBlocks[i];

          // Get the gap that should appear AFTER this block
          const gapValue = currentBlock.gapLocked
            ? design.global_gap || 0
            : (currentBlock.gap ?? 0);

          // Add the gap to our sequence
          gaps.push(gapValue);

          // Add special handling for stacked containers
          if (i === 0 && hasStackedContainer && currentBlock.verticalStack) {
            // If this is a stacked container, also add its internal vertical gap
            if (currentBlock.blocks && currentBlock.blocks.length > 0) {
              // Add vertical gap between stacked characters
              for (let j = 0; j < currentBlock.blocks.length - 1; j++) {
                const stackedBlock = currentBlock.blocks[j];
                if (stackedBlock.verticalGap) {
                  gaps.push(stackedBlock.verticalGap);
                }
              }
            }
          }
        }

        // Recursively process nested containers that aren't stacked
        allBlocks.forEach((childBlock) => {
          if (
            childBlock.isContainer &&
            !childBlock.verticalStack &&
            childBlock.blocks
          ) {
            gaps.push(...extractGaps([childBlock]));
          }
        });
      }
    });

    return gaps;
  };

  const newGaps = extractGaps(updatedDesign.blocks);

  // Return updated hiVis
  return {
    ...hiVis,
    design: updatedDesign,
    gap: newGaps,
  };
};

/**
 * Update a specific block in the HiVis design
 * @param hiVis The HiVis object to update
 * @param updatedBlock The updated block
 * @param path Path to the block to update
 * @returns Updated HiVis object
 */
export const updateBlock = (
  hiVis: HiVis,
  updatedBlock: Block,
  path: number[]
): HiVis => {
  // Create deep copy of hiVis to prevent mutation
  const hiVisCopy = cloneHiVis(hiVis);

  // Create a deep copy of the design blocks
  const newBlocks = [...hiVisCopy.design.blocks];

  // Helper function to update nested blocks
  const updateNestedBlock = (
    blocks: Block[],
    pathIndices: number[],
    index: number,
    blockToUpdate: Block
  ): Block[] => {
    if (index >= pathIndices.length - 1) {
      blocks[pathIndices[index]] = blockToUpdate;
      return blocks;
    }

    const newBlocks = [...blocks];
    const currentBlock = { ...newBlocks[pathIndices[index]] };

    if (!currentBlock.blocks) {
      currentBlock.blocks = [];
    }

    currentBlock.blocks = updateNestedBlock(
      currentBlock.blocks,
      pathIndices,
      index + 1,
      blockToUpdate
    );

    newBlocks[pathIndices[index]] = currentBlock;
    return newBlocks;
  };

  // Update the blocks at the specified path
  const updatedBlocks = updateNestedBlock(newBlocks, path, 0, updatedBlock);

  // Create updated design
  const updatedDesign = {
    ...hiVisCopy.design,
    blocks: updatedBlocks,
  };

  // Update the HiVis copy with the new design and gaps
  hiVisCopy.design = updatedDesign;

  // Extract gaps from the updated blocks
  hiVisCopy.gap = extractGapsFromBlocks(updatedDesign.blocks);

  return hiVisCopy;
};

/**
 * Delete block from HiVis design
 */
export const deleteBlock = (hiVis: HiVis, path: number[]): HiVis => {
  if (path.length < 2) return hiVis; // Don't delete root blocks

  // Create deep copy of design to prevent mutation
  const updatedDesign: Design = JSON.parse(JSON.stringify(hiVis.design));

  // Helper function to delete nested blocks
  const deleteNestedBlock = (
    blocks: Block[],
    pathIndices: number[],
    index: number
  ): Block[] => {
    if (index >= pathIndices.length - 2) {
      // We're at the parent of the block to delete
      const parentBlock = { ...blocks[pathIndices[index]] };
      if (!parentBlock.blocks) return blocks;

      const deletedBlock = parentBlock.blocks[pathIndices[index + 1]];

      // If the deleted block is a container, move its character blocks to parent container
      if (deletedBlock.isContainer && deletedBlock.blocks) {
        // Filter out nested blocks that should be preserved
        const characterBlocks = deletedBlock.blocks;

        parentBlock.blocks = [
          ...parentBlock.blocks.slice(0, pathIndices[index + 1]),
          ...characterBlocks.map((childBlock) => {
            // Create a cleaned block with proper properties
            const cleanedBlock: Block = {
              ...childBlock,
              color: parentBlock.color,
            };

            // Remove isContainer property from character blocks
            if (childBlock.character) {
              delete cleanedBlock.isContainer;
            }

            // Preserve container properties for actual containers
            if (childBlock.isContainer) {
              cleanedBlock.isContainer = true;
              // Ensure blocks property is preserved
              if (childBlock.blocks) {
                cleanedBlock.blocks = childBlock.blocks;
              }
            }

            return cleanedBlock;
          }),
          ...parentBlock.blocks.slice(pathIndices[index + 1] + 1),
        ];
      } else {
        // Filter out the block to delete
        parentBlock.blocks = parentBlock.blocks.filter(
          (_, i) => i !== pathIndices[index + 1]
        );

        // Redistribute dimensions
        const isVertical = parentBlock.width < parentBlock.height;
        if (parentBlock.blocks.length > 0) {
          if (isVertical) {
            // Redistribute height
            const heightPerBlock =
              parentBlock.height / parentBlock.blocks.length;
            parentBlock.blocks.forEach((childBlock) => {
              childBlock.width = parentBlock.width;
              childBlock.height = heightPerBlock;
              if (childBlock.blocks) {
                childBlock.blocks = childBlock.blocks.map((nestedBlock) => {
                  const updatedBlock = {
                    ...nestedBlock,
                    width: parentBlock.width,
                    height:
                      nestedBlock.height * (heightPerBlock / childBlock.height),
                  };

                  // Remove isContainer property from character blocks
                  if (nestedBlock.character) {
                    delete updatedBlock.isContainer;
                  }

                  return updatedBlock;
                });
              }
            });
          } else {
            // Redistribute width
            const widthPerBlock = parentBlock.width / parentBlock.blocks.length;
            parentBlock.blocks.forEach((childBlock) => {
              childBlock.width = widthPerBlock;
              childBlock.height = parentBlock.height;
              if (childBlock.blocks) {
                childBlock.blocks = childBlock.blocks.map((nestedBlock) => {
                  const updatedBlock = {
                    ...nestedBlock,
                    width:
                      nestedBlock.width * (widthPerBlock / childBlock.width),
                    height: parentBlock.height,
                  };

                  // Remove isContainer property from character blocks
                  if (nestedBlock.character) {
                    delete updatedBlock.isContainer;
                  }

                  return updatedBlock;
                });
              }
            });
          }
        }
      }

      blocks[pathIndices[index]] = parentBlock;
      return blocks;
    }

    const newBlocks = [...blocks];
    const blockToUpdate = { ...newBlocks[pathIndices[index]] };

    if (!blockToUpdate.blocks) return blocks;

    blockToUpdate.blocks = deleteNestedBlock(
      blockToUpdate.blocks,
      pathIndices,
      index + 1
    );

    newBlocks[pathIndices[index]] = blockToUpdate;
    return newBlocks;
  };

  // Delete the block at the specified path
  updatedDesign.blocks = deleteNestedBlock(updatedDesign.blocks, path, 0);

  // Clean up any inappropriate character properties on container blocks
  const cleanupDesign = (blocks: Block[]): Block[] => {
    return blocks.map((block) => {
      const cleanedBlock = { ...block };

      // If it's a container, it shouldn't have a character property
      if (cleanedBlock.isContainer && cleanedBlock.character) {
        delete cleanedBlock.character;
      }

      // Remove isContainer from character blocks
      if (cleanedBlock.character && cleanedBlock.isContainer === undefined) {
        delete cleanedBlock.isContainer;
      }

      // Recursively clean nested blocks
      if (cleanedBlock.blocks && cleanedBlock.blocks.length > 0) {
        cleanedBlock.blocks = cleanupDesign(cleanedBlock.blocks);
      }

      return cleanedBlock;
    });
  };

  updatedDesign.blocks = cleanupDesign(updatedDesign.blocks);

  // Extract gaps from the updated blocks
  const newGaps = extractGapsFromBlocks(updatedDesign.blocks);

  // Ensure we maintain the global_gap property
  updatedDesign.global_gap = hiVis.design.global_gap;

  // Return updated hiVis
  return {
    ...hiVis,
    design: updatedDesign,
    gap: newGaps.length > 0 ? newGaps : hiVis.gap, // Preserve original gaps if extraction fails
  };
};

/**
 * Update material color in a HiVis design or a specific block
 * @param hiVis Original HiVis object
 * @param materialColor New material color to apply
 * @param targetBlock Optional specific block to update (if not provided, updates root block)
 * @param baseMaterialColor Optional reference color for the base material (defaults to materialColor)
 * @returns If targetBlock is provided, returns the updated block; otherwise returns updated HiVis
 */
export const updateBaseMaterial = (
  hiVis: HiVis | Block,
  materialColor: string,
  targetBlock?: Block,
  baseMaterialColor?: string
): HiVis | Block => {
  // Helper function to recursively update colors in blocks and their children
  const updateBlockColors = (block: Block): Block => {
    // If this is a container (including nested), update its color
    const updatedBlock = {
      ...block,
      color: materialColor,
    };

    // Update children blocks if they exist
    if (updatedBlock.blocks && updatedBlock.blocks.length > 0) {
      updatedBlock.blocks = updatedBlock.blocks.map((childBlock) => {
        // For character blocks, handle color logic differently
        if (childBlock.character !== undefined) {
          // For character SVGs, they should be opposite of their container
          const referenceColor = baseMaterialColor || materialColor;
          return {
            ...childBlock,
            color:
              updatedBlock.color === "#000000" ? "#000000" : referenceColor,
          };
        }
        // Recursively update other blocks (containers)
        return updateBlockColors(childBlock);
      });
    }
    return updatedBlock;
  };

  // If we're just updating a single block
  if (targetBlock) {
    return updateBlockColors(targetBlock);
  }

  // Otherwise update the whole HiVis
  if ("design" in hiVis) {
    // Create deep copy of hiVis to prevent mutation
    const updatedHiVis = cloneHiVis(hiVis);

    // Check if the design has blocks
    if (updatedHiVis.design.blocks.length > 0) {
      // Update all blocks (not just the root)
      updatedHiVis.design.blocks = updatedHiVis.design.blocks.map((block) =>
        updateBlockColors(block)
      );
    }

    return updatedHiVis;
  }

  // If hiVis was actually just a block, update it directly
  return updateBlockColors(hiVis);
};

/**
 * Handle dimension changes for a block and its children
 * @param block The block to update
 * @param dimension The dimension to change ('width' or 'height')
 * @param newValue The new value for the dimension
 * @param path Current path to the block
 * @param initialRatios Map of initial dimension ratios
 * @param useNaturalWidth Whether to use natural width for character blocks
 * @returns Updated block with new dimensions
 */
export const updateBlockDimensions = (
  block: Block,
  dimension: "width" | "height",
  newValue: number,
  path: number[] = [],
  initialRatios: Map<string, number> = new Map(),
  useNaturalWidth: boolean = true
): Block => {
  // Round the new value
  newValue = Math.round(newValue);

  // Helper function to update block dimensions recursively
  const updateDimensions = (
    block: Block,
    parentDimension: number,
    blockOriginalWidth: number,
    blockOriginalHeight: number,
    currentPath: number[] = []
  ): Block => {
    // Create updated block with new dimension
    const updatedBlock = {
      ...block,
      [dimension]: parentDimension,
    };

    // If height is changing, adjust corner radius to maintain proportion
    if (dimension === "height" && block.cornerRadius) {
      const maxRadius = Math.min(parentDimension, block.width) / 2;
      updatedBlock.cornerRadius = Math.min(block.cornerRadius, maxRadius);
    }

    // Update child blocks if present
    if (block.blocks) {
      const characterBlocks = block.blocks.filter((b) => !b.isContainer);
      const containerBlocks = block.blocks.filter((b) => b.isContainer);

      // Handle stacked containers and other containers differently
      const stackedContainers = containerBlocks.filter((b) => b.verticalStack);
      const regularContainers = containerBlocks.filter((b) => !b.verticalStack);

      // Check orientation using the block's original dimensions
      const isVertical = blockOriginalWidth < blockOriginalHeight;
      const hasCharacters = characterBlocks.length > 0;
      const hasStackedContainers = stackedContainers.length > 0;

      // Calculate total gaps for character blocks
      const characterGaps = hasCharacters
        ? characterBlocks.reduce((sum, b, i) => {
            if (i < characterBlocks.length - 1) {
              return sum + (b.gapLocked ? 0 : (b.gap ?? 0));
            }
            return sum;
          }, 0)
        : 0;

      // Calculate gaps for stacked containers (always use global gap)
      const stackedContainerGaps = hasStackedContainers
        ? stackedContainers.reduce((sum, b, i) => {
            if (i < stackedContainers.length - 1) {
              return sum + 0; // Default gap of 0 for stacked containers
            }
            return sum;
          }, 0)
        : 0;

      // Calculate total effective blocks for width distribution
      // Stacked containers count as single blocks for horizontal distribution
      const effectiveBlockCount =
        characterBlocks.length + stackedContainers.length;

      // Calculate total gaps for all blocks that will be horizontally distributed
      const totalGaps = characterGaps + stackedContainerGaps;

      // Available space after accounting for gaps
      const availableSpace =
        dimension === "width"
          ? parentDimension - totalGaps
          : parentDimension - totalGaps;

      if (hasCharacters) {
        if (isVertical) {
          // For vertical layouts
          const blockHeight = availableSpace / characterBlocks.length;

          characterBlocks.forEach((b) => {
            if (dimension === "height") {
              // When height changes in a vertical layout
              b.height = Math.round(blockHeight);

              // If using natural width, recalculate width based on new height
              if (useNaturalWidth && b.character) {
                const naturalRatio = getNaturalWidthRatio(b.character);
                const naturalWidth = b.height * naturalRatio;

                // If natural width is less than container width, use that
                if (naturalWidth < parentDimension) {
                  b.width = Math.round(naturalWidth);
                } else {
                  // Otherwise use full container width
                  b.width = parentDimension;
                }
              } else {
                // If not using natural width, use full container width
                b.width = parentDimension;
              }
            } else if (dimension === "width") {
              // When width changes
              b.width = isVertical
                ? parentDimension
                : Math.round(availableSpace / characterBlocks.length);

              // For vertical layouts with natural width, we need to check if we should reduce width
              if (isVertical && useNaturalWidth && b.character) {
                const naturalRatio = getNaturalWidthRatio(b.character);
                const naturalWidth = b.height * naturalRatio;

                // Only use natural width if it's less than the container width
                if (naturalWidth < parentDimension) {
                  b.width = Math.round(naturalWidth);
                }
              }
            }
          });
        } else if (useNaturalWidth && !isVertical && dimension === "width") {
          // Use natural width distribution for horizontal layouts
          const totalNaturalRatio = characterBlocks.reduce((sum, b) => {
            return sum + getNaturalWidthRatio(b.character || "");
          }, 0);

          // Update character blocks with natural width distribution
          characterBlocks.forEach((b) => {
            const naturalRatio = getNaturalWidthRatio(b.character || "");
            b.width = Math.round(
              (availableSpace * naturalRatio) / totalNaturalRatio
            );
            b.height = parentDimension;
          });
        } else {
          // Use uniform distribution for non-natural width mode
          const blockSize = availableSpace / effectiveBlockCount;
          characterBlocks.forEach((b) => {
            if (dimension === "width") {
              b.width = isVertical ? parentDimension : Math.round(blockSize);
            } else {
              b.height = isVertical ? Math.round(blockSize) : parentDimension;
            }
          });
        }
      }

      // Handle stacked containers
      if (hasStackedContainers) {
        stackedContainers.forEach((stackedContainer) => {
          if (dimension === "width") {
            // For horizontal layouts, stacked containers get equal width like other blocks
            if (!isVertical) {
              const blockSize = availableSpace / effectiveBlockCount;
              // Set the width of the stacked container
              stackedContainer.width = Math.round(blockSize);

              // Also update all children inside the stacked container to match the new width
              if (stackedContainer.blocks) {
                stackedContainer.blocks.forEach((nestedBlock) => {
                  nestedBlock.width = stackedContainer.width;
                });
              }
            } else {
              // For vertical layouts, stacked containers take full parent width
              stackedContainer.width = parentDimension;

              // Also update all children inside the stacked container
              if (stackedContainer.blocks) {
                stackedContainer.blocks.forEach((nestedBlock) => {
                  nestedBlock.width = parentDimension;
                });
              }
            }
          } else if (dimension === "height") {
            // Height changes affect the stacked container height
            // For horizontal layouts, stacked containers take full parent height
            stackedContainer.height = parentDimension;

            // Update the height of each block in the stack
            if (stackedContainer.blocks) {
              const stackHeight =
                parentDimension / stackedContainer.blocks.length;
              stackedContainer.blocks.forEach((nestedBlock) => {
                nestedBlock.height = stackHeight;
              });
            }
          }
        });
      }

      // Process regular (non-stacked) container blocks with border-aware scaling
      const processedContainers = regularContainers.map(
        (childBlock, childIndex) => {
          // Determine the child path
          const childPath = [...currentPath, childIndex];
          const childPathStr = childPath.join("-");

          // Create a copy of the block to modify
          const blockCopy = { ...childBlock };

          // Store the child's original dimensions for its own children
          const childOriginalWidth = childBlock.width;
          const childOriginalHeight = childBlock.height;

          // Calculate border spacing (difference between parent and child)
          const originalWidthBorder = blockOriginalWidth - childBlock.width;
          const originalHeightBorder = blockOriginalHeight - childBlock.height;

          // Determine if this looks like a border relationship or a ratio relationship
          const isBorderRelationship = (
            border: number,
            childDimension: number,
            parentDimension: number
          ) => {
            // Absolute border value should be reasonable (between 1mm and 200mm)
            const absoluteBorderOk =
              Math.abs(border) >= 1 && Math.abs(border) <= 200;

            if (!absoluteBorderOk) return false;

            // For very small containers, be more lenient with border ratio
            if (parentDimension < 200) {
              // For small containers, allow up to 40% border ratio
              const borderRatio =
                Math.abs(border) / Math.max(childDimension, 1);
              return borderRatio > 0 && borderRatio < 0.4;
            }

            // For normal-sized containers, use standard criteria
            const borderRatio = Math.abs(border) / Math.max(childDimension, 1);

            // If the border is small relative to the child dimension (< 25% of child size)
            // and is a reasonable absolute value, treat it as border spacing
            return borderRatio > 0 && borderRatio < 0.25;
          };

          if (dimension === "width") {
            let newChildDimension: number;

            // Check if this was previously identified as a border relationship
            const wasBorderRelationship = blockCopy.isBorderWidth === true;
            const isBorderNow = isBorderRelationship(
              originalWidthBorder,
              childBlock.width,
              blockOriginalWidth
            );

            if (wasBorderRelationship || isBorderNow) {
              // Maintain border spacing in absolute millimeters
              newChildDimension = Math.round(
                parentDimension - originalWidthBorder
              );

              // Ensure the child doesn't become negative or larger than parent
              newChildDimension = Math.max(
                10,
                Math.min(newChildDimension, parentDimension - 2)
              );

              // Mark this as a border relationship for future scaling
              blockCopy.isBorderWidth = true;
            } else {
              // Use ratio-based scaling for non-border relationships
              let ratio: number;

              // Prevent division by zero or very small numbers
              if (blockOriginalWidth <= 0) {
                console.warn(
                  `Invalid original width: ${blockOriginalWidth}, using fallback ratio`
                );
                ratio = 0.5;
              } else {
                // Check stored ratio first, then calculate
                if (
                  blockCopy.widthRatio !== undefined &&
                  blockCopy.widthRatio > 0 &&
                  blockCopy.widthRatio <= 1
                ) {
                  ratio = blockCopy.widthRatio;
                } else if (initialRatios.has(`${childPathStr}-width`)) {
                  const storedRatio = initialRatios.get(
                    `${childPathStr}-width`
                  )!;
                  ratio =
                    storedRatio > 0 && storedRatio <= 1
                      ? storedRatio
                      : childBlock.width / blockOriginalWidth;
                } else {
                  ratio = childBlock.width / blockOriginalWidth;
                }

                // First, check if dimensions are equal (within 1mm tolerance)
                if (Math.abs(childBlock.width - blockOriginalWidth) <= 1) {
                  ratio = 1.0;
                } else {
                  // Fix precision issues - if ratio is very close to 1.0, make it exactly 1.0
                  if (Math.abs(ratio - 1.0) < 0.001) {
                    ratio = 1.0;
                  }

                  // Fix precision issues - if ratio is very close to common fractions, snap to them
                  const commonRatios = [
                    0.1, 0.125, 0.2, 0.25, 0.333, 0.5, 0.666, 0.75, 0.8, 0.875,
                    0.9, 1.0,
                  ];
                  for (const commonRatio of commonRatios) {
                    if (Math.abs(ratio - commonRatio) < 0.002) {
                      ratio = commonRatio;
                      break;
                    }
                  }
                }

                // Clamp ratio to reasonable bounds
                ratio = Math.max(0.01, Math.min(1, ratio));
              }

              // Apply ratio to get new width with proper rounding
              const exactNewDimension = parentDimension * ratio;
              newChildDimension = Math.round(exactNewDimension);

              // Store the precise ratio for future resizing
              blockCopy.widthRatio = ratio;

              // Mark this as NOT a border relationship
              blockCopy.isBorderWidth = false;
            }

            blockCopy.width = newChildDimension;
          } else {
            // Height dimension
            let newChildDimension: number;

            // Check if this was previously identified as a border relationship
            const wasBorderRelationship = blockCopy.isBorderHeight === true;
            const isBorderNow = isBorderRelationship(
              originalHeightBorder,
              childBlock.height,
              blockOriginalHeight
            );

            if (wasBorderRelationship || isBorderNow) {
              // Maintain border spacing in absolute millimeters
              newChildDimension = Math.round(
                parentDimension - originalHeightBorder
              );

              // Ensure the child doesn't become negative or larger than parent
              newChildDimension = Math.max(
                10,
                Math.min(newChildDimension, parentDimension - 2)
              );

              // Mark this as a border relationship for future scaling
              blockCopy.isBorderHeight = true;
            } else {
              // Use ratio-based scaling for non-border relationships
              let ratio: number;

              if (blockOriginalHeight <= 0) {
                console.warn(
                  `Invalid original height: ${blockOriginalHeight}, using fallback ratio`
                );
                ratio = 0.5;
              } else {
                if (
                  blockCopy.heightRatio !== undefined &&
                  blockCopy.heightRatio > 0 &&
                  blockCopy.heightRatio <= 1
                ) {
                  ratio = blockCopy.heightRatio;
                } else if (initialRatios.has(`${childPathStr}-height`)) {
                  const storedRatio = initialRatios.get(
                    `${childPathStr}-height`
                  )!;
                  ratio =
                    storedRatio > 0 && storedRatio <= 1
                      ? storedRatio
                      : childBlock.height / blockOriginalHeight;
                } else {
                  ratio = childBlock.height / blockOriginalHeight;
                }

                // First, check if dimensions are equal (within 1mm tolerance)
                if (Math.abs(childBlock.height - blockOriginalHeight) <= 1) {
                  ratio = 1.0;
                } else {
                  // Fix precision issues
                  if (Math.abs(ratio - 1.0) < 0.001) {
                    ratio = 1.0;
                  }

                  const commonRatios = [
                    0.1, 0.125, 0.2, 0.25, 0.333, 0.5, 0.666, 0.75, 0.8, 0.875,
                    0.9, 1.0,
                  ];
                  for (const commonRatio of commonRatios) {
                    if (Math.abs(ratio - commonRatio) < 0.002) {
                      ratio = commonRatio;
                      break;
                    }
                  }
                }

                ratio = Math.max(0.01, Math.min(1, ratio));
              }

              const exactNewDimension = parentDimension * ratio;
              newChildDimension = Math.round(exactNewDimension);

              // Store the precise ratio for future resizing
              blockCopy.heightRatio = ratio;

              // Mark this as NOT a border relationship
              blockCopy.isBorderHeight = false;
            }

            blockCopy.height = newChildDimension;
          }

          // Recursively update child blocks using the child's original dimensions
          return updateDimensions(
            blockCopy,
            blockCopy[dimension],
            childOriginalWidth,
            childOriginalHeight,
            childPath
          );
        }
      );

      updatedBlock.blocks = [
        ...processedContainers,
        ...stackedContainers,
        ...characterBlocks,
      ];
    }

    return updatedBlock;
  };

  // Update the block and all its children, starting with the block's original dimensions
  const updatedBlock = updateDimensions(
    block,
    newValue,
    block.width,
    block.height,
    path
  );

  // Ensure ratios are preserved and clamped after updating
  if (updatedBlock.blocks) {
    updatedBlock.blocks.forEach((childBlock) => {
      if (childBlock.isContainer && !childBlock.verticalStack) {
        // Ensure both width and height ratios are preserved and reasonable
        if (
          childBlock.widthRatio === undefined ||
          childBlock.widthRatio <= 0 ||
          childBlock.widthRatio > 1
        ) {
          childBlock.widthRatio = Math.max(
            0.01,
            Math.min(1, childBlock.width / updatedBlock.width)
          );
        }

        if (
          childBlock.heightRatio === undefined ||
          childBlock.heightRatio <= 0 ||
          childBlock.heightRatio > 1
        ) {
          childBlock.heightRatio = Math.max(
            0.01,
            Math.min(1, childBlock.height / updatedBlock.height)
          );
        }
      }
    });
  }

  return updatedBlock;
};

/**
 * Get material information for a block
 * @param block The block to check
 * @returns Material value (black or base)
 */
export const getMaterialForBlock = (block: Block): string => {
  return block.color === "#000000" ? "black" : "base";
};

/**
 * Get initial dimension ratios for all blocks in a hierarchy
 * @param block The root block
 * @param dimension The dimension to get ratios for ('width' or 'height')
 * @param currentPath Current path to the block
 * @returns Map of path to ratio
 */
export const getBlockDimensionRatios = (
  block: Block,
  dimension: "width" | "height",
  currentPath: number[] = []
): Map<string, number> => {
  const ratios = new Map<string, number>();
  if (block.blocks) {
    block.blocks.forEach((childBlock, index) => {
      const childPath = [...currentPath, index];
      // Store ratio for this block regardless of type
      const pathKey = childPath.join("-");

      // Always store both width and height ratios
      const widthRatio = childBlock.width / block.width;
      const heightRatio = childBlock.height / block.height;

      // Store the ratio for the dimension being changed
      ratios.set(pathKey, dimension === "width" ? widthRatio : heightRatio);

      // Also store the explicit width and height ratios with suffixes
      ratios.set(`${pathKey}-width`, widthRatio);
      ratios.set(`${pathKey}-height`, heightRatio);

      // Recursively get ratios for children
      if (childBlock.blocks && childBlock.blocks.length > 0) {
        const childRatios = getBlockDimensionRatios(
          childBlock,
          dimension,
          childPath
        );
        childRatios.forEach((value, key) => ratios.set(key, value));
      }
    });
  }
  return ratios;
};

/**
 * Determine if a block is selected
 * @param currentPath Path to the current block
 * @param selectedPath Path to the selected block
 * @returns True if the block is selected
 */
export const isBlockSelected = (
  currentPath: number[],
  selectedPath: number[] = []
): boolean => {
  return (
    selectedPath.length > 0 &&
    currentPath.length === selectedPath.length &&
    currentPath.every((value, i) => value === selectedPath[i])
  );
};

/**
 * Determine if a block is active
 * @param currentPath Path to the current block
 * @param activePath Path to the active block
 * @returns True if the block is active
 */
export const isBlockActive = (
  currentPath: number[],
  activePath: number[] = []
): boolean => {
  return (
    activePath?.length === currentPath.length &&
    currentPath.every((value, i) => value === activePath[i])
  );
};

/**
 * Determine if a block has a container child
 * @param block The block to check
 * @returns True if the block has a container child
 */
export const hasContainerChild = (block: Block): boolean => {
  return (
    block.blocks?.some((childBlock) => childBlock.isContainer === true) || false
  );
};

/**
 * Determine if a block is a character container
 * @param block The block to check
 * @returns True if the block directly contains character blocks
 */
export const isCharacterContainer = (block: Block): boolean => {
  if (!block.blocks) return false;
  return block.blocks.some(
    (b) =>
      b.character !== undefined ||
      (b.isContainer &&
        b.verticalStack &&
        b.blocks?.some((c) => c.character !== undefined))
  );
};

/**
 * Determine if a block has character containers in its children
 * @param block The block to check
 * @returns True if the block has character containers in its children
 */
export const hasCharacterContainers = (block: Block): boolean => {
  if (!block.blocks) return false;
  return block.blocks.some(
    (b) =>
      isCharacterContainer(b) || (b.isContainer && hasCharacterContainers(b))
  );
};

/**
 * Determine if a block is the root container
 * @param currentPath Path to the current block
 * @returns True if the block is the root container
 */
export const isRootContainer = (currentPath: number[]): boolean => {
  return currentPath.length === 1;
};

/**
 * Determine if a container should show material select
 * @param block The block to check
 * @param currentPath Path to the current block
 * @returns True if the container should show material select
 */
export const shouldShowMaterialSelect = (
  block: Block,
  currentPath: number[]
): boolean => {
  // Root container (Container 1) never shows material select
  if (isRootContainer(currentPath)) return false;

  // Show material select for:
  // 1. Character containers (containers that directly hold characters)
  // 2. Containers that have character containers as children (like Container 2)
  return isCharacterContainer(block) || hasCharacterContainers(block);
};

/**
 * Creates a new block with a container
 * @param block The parent block
 * @returns Updated block with a new container
 */
export const addContainerBlock = (block: Block): Block => {
  // Create a new container block with an empty blocks array
  const newBlock: Block = {
    width: Math.round(block.width),
    height: Math.round(block.height),
    color: block.color,
    blocks: [], // Initialize empty blocks array
    isContainer: true, // Explicitly mark as container
  };

  // If there are existing blocks in the current container,
  // move them to the new container's blocks array
  if (block.blocks && block.blocks.length > 0) {
    // Move all existing blocks (characters) into the new container's blocks array
    newBlock.blocks = block.blocks.map((childBlock) => ({
      ...childBlock,
      width: childBlock.width,
      height: childBlock.height,
      color: childBlock.color,
      character: childBlock.character,
      gap: childBlock.gap,
      gapLocked: childBlock.gapLocked,
    }));
  }

  // Update the current block to have only the new container as its child
  return {
    ...block,
    blocks: [newBlock],
  };
};

/**
 * Update global gap for all child blocks
 * @param block The parent block
 * @param global_gapValue The new global gap value
 * @returns Updated block with new global gap
 */
export const updateGlobalGap = (
  block: Block,
  global_gapValue: number
): Block => {
  // Update all gaps in the block's children to match the global gap
  const updatedBlocks = block.blocks?.map((b) => {
    // Only update gaps of locked blocks
    return {
      ...b,
      gap: b.gapLocked ? global_gapValue : b.gap,
    };
  });

  return {
    ...block,
    blocks: updatedBlocks,
  };
};

/**
 * Update vertical gap for container
 * @param block The parent block
 * @param verticalGapValue The new vertical gap value
 * @returns Updated block with new vertical gap
 */
export const updateVerticalGap = (
  block: Block,
  verticalGapValue: number
): Block => {
  // Update vertical gap in the block
  return {
    ...block,
    verticalGap: verticalGapValue,
    // Also update vertical gaps of child blocks if they exist
    blocks: block.blocks?.map((childBlock) => ({
      ...childBlock,
      verticalGap:
        childBlock.verticalGap !== undefined ? verticalGapValue : undefined,
    })),
  };
};

/**
 * Update corner radius for a block
 * @param block The block to update
 * @param cornerRadiusValue The new corner radius value
 * @returns Updated block with new corner radius
 */
export const updateCornerRadius = (
  block: Block,
  cornerRadiusValue: number
): Block => {
  return {
    ...block,
    cornerRadius: cornerRadiusValue,
  };
};

/**
 * Extract all gaps from a block hierarchy for the HiVis gap property
 * @param blocks Array of blocks to extract gaps from
 * @returns Array of gap values
 */
export const extractGapsFromBlocks = (
  blocks: Block[],
  global_gap: number = 0
): number[] => {
  const gaps: number[] = [];

  // Recursive function to extract gaps from all blocks
  const processBlock = (block: Block, globalGap: number = 0) => {
    if (!block.blocks || block.blocks.length === 0) return;

    // Process all blocks that have gaps between them
    for (let i = 0; i < block.blocks.length - 1; i++) {
      const currentBlock = block.blocks[i];

      // Get the gap that should appear AFTER this block
      const gapValue = currentBlock.gapLocked
        ? globalGap // Use global gap when locked
        : (currentBlock.gap ?? globalGap); // Use block gap or fallback to global

      gaps.push(gapValue);

      // Process vertical stacked containers specially
      if (currentBlock.isContainer && currentBlock.verticalStack) {
        if (currentBlock.blocks) {
          // Process gaps between vertically stacked blocks
          for (let j = 0; j < currentBlock.blocks.length - 1; j++) {
            const stackedBlock = currentBlock.blocks[j];
            const stackedGap = stackedBlock.verticalGap ?? globalGap;
            gaps.push(stackedGap);
          }
        }
      }

      // Recurse into container blocks
      if (currentBlock.isContainer && currentBlock.blocks) {
        processBlock(currentBlock, globalGap);
      }
    }

    // Process the last block (which doesn't have a gap after it)
    const lastBlock = block.blocks[block.blocks.length - 1];
    if (lastBlock && lastBlock.isContainer && lastBlock.blocks) {
      processBlock(lastBlock, globalGap);
    }
  };

  // Start processing from each top-level block
  blocks.forEach((block) => {
    processBlock(block, global_gap);
  });

  // If no gaps were found but we have blocks with characters, create default gaps
  if (gaps.length === 0) {
    // Find blocks with characters and create default gaps
    const findCharBlocks = (block: Block): number => {
      let charCount = 0;

      if (block.character) {
        return 1;
      }

      if (block.blocks) {
        block.blocks.forEach((childBlock) => {
          charCount += findCharBlocks(childBlock);
        });
      }

      return charCount;
    };

    const charCount = findCharBlocks({ blocks, width: 0, height: 0 });

    // If we have characters, create default gaps
    if (charCount > 0) {
      // Create one fewer gaps than characters, using the provided global_gap or default to 3
      for (let i = 0; i < charCount - 1; i++) {
        gaps.push(global_gap ?? 3);
      }
    }
  }

  return gaps;
};

/**
 * Toggle gap lock for a block
 * @param parentBlock The parent block
 * @param blockIndex The index of the block to toggle
 * @returns Updated parent block with toggled gap lock
 */
export const toggleGapLock = (
  parentBlock: Block,
  blockIndex: number
): Block => {
  const newBlocks = [...parentBlock.blocks!];
  const childBlock = newBlocks[blockIndex];

  newBlocks[blockIndex] = {
    ...childBlock,
    gapLocked: !childBlock.gapLocked,
    gap: 0, // Default to 0 when toggling gap lock
  };

  return {
    ...parentBlock,
    blocks: newBlocks,
  };
};

/**
 * Update gap for a specific block
 * @param parentBlock The parent block
 * @param blockIndex The index of the block to update
 * @param gapValue The new gap value
 * @returns Updated parent block with new gap
 */
export const updateBlockGap = (
  parentBlock: Block,
  blockIndex: number,
  gapValue: number
): Block => {
  const newBlocks = [...parentBlock.blocks!];
  newBlocks[blockIndex] = {
    ...newBlocks[blockIndex],
    gap: gapValue,
  };

  return {
    ...parentBlock,
    blocks: newBlocks,
  };
};

/**
 * Analyze a design to determine if it has stacked first characters
 * This is used when loading a design to set the stackFirstTwo property correctly
 */
export const analyzeDesignForStacking = (design: Design): boolean => {
  // Find the character container in the design structure
  const findCharacterContainer = (blocks: Block[]): Block | null => {
    for (const block of blocks) {
      // Check if this block's children contain character blocks
      if (block.blocks?.some((b) => b.character !== undefined)) {
        return block;
      }

      // Recursively check nested blocks
      if (block.blocks && block.blocks.length > 0) {
        const result = findCharacterContainer(block.blocks);
        if (result) {
          return result;
        }
      }
    }
    return null;
  };

  const characterContainer = findCharacterContainer(design.blocks);
  if (!characterContainer?.blocks) {
    return false;
  }

  // Check if the first block in the character container is a vertical stack
  const firstBlock = characterContainer.blocks[0];
  if (firstBlock?.isContainer && firstBlock?.verticalStack) {
    // Check if it contains exactly 2 character blocks
    const charBlocks =
      firstBlock.blocks?.filter((b) => b.character !== undefined) || [];
    return charBlocks.length === 2;
  }

  return false;
};

/**
 * Synchronize HiVis properties with design structure
 * This ensures UI controls reflect the actual design state
 */
export const syncHiVisWithDesign = (hiVis: HiVis): HiVis => {
  const updatedHiVis = cloneHiVis(hiVis);

  // Check if the design indicates stacking and update stackFirstTwo accordingly
  const hasStacking = analyzeDesignForStacking(updatedHiVis.design);
  updatedHiVis.stackFirstTwo = hasStacking;

  return updatedHiVis;
};
